import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Mail,
  ShieldCheck,
  Smartphone,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo.jsx'
import { authApi } from '../api/modules/authApi.js'
import { SUPPORT_EMAIL } from '../constants/api.js'
import {
  clearError,
  googleLoginThunk,
  loginThunk,
  loginWithOtpThunk,
} from '../store/authSlice.js'
import { hasGoogleClientId, loadGoogleIdentity } from '../utils/googleIdentity.js'

const OTP_RESEND_SECONDS = 60

const getErrorMessage = (error, fallback) => (
  error?.response?.data?.message
  || error?.message
  || fallback
)

export default function LoginPage() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const googleButtonRef = useRef(null)

  const { loading, error, isAuthenticated } = useSelector((state) => state.auth)

  const [authMethod, setAuthMethod] = useState('password')
  const [activeAction, setActiveAction] = useState(null)
  const [form, setForm] = useState({ email: '', password: '' })
  const [mobile, setMobile] = useState('')
  const [otp, setOtp] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [errors, setErrors] = useState({})
  const [localError, setLocalError] = useState('')
  const [googleError, setGoogleError] = useState('')
  const [googleReady, setGoogleReady] = useState(false)

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true })
  }, [isAuthenticated, navigate])

  useEffect(() => () => { dispatch(clearError()) }, [dispatch])

  useEffect(() => {
    if (!loading) {
      setActiveAction(null)
    }
  }, [loading])

  useEffect(() => {
    if (countdown <= 0) return undefined

    const timer = window.setTimeout(() => {
      setCountdown((value) => Math.max(value - 1, 0))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [countdown])

  useEffect(() => {
    if (!hasGoogleClientId || !googleButtonRef.current) return undefined

    let cancelled = false

    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !googleButtonRef.current) return

        google.accounts.id.initialize({
          client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
          callback: ({ credential }) => {
            if (!credential) {
              setGoogleError('Google sign-in did not return a valid token.')
              return
            }

            setGoogleError('')
            setLocalError('')
            dispatch(clearError())
            setActiveAction('google')
            dispatch(googleLoginThunk({ googleToken: credential }))
          },
        })

        googleButtonRef.current.innerHTML = ''
        google.accounts.id.renderButton(googleButtonRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          width: Math.min(380, googleButtonRef.current.offsetWidth || 380),
        })

        setGoogleReady(true)
      })
      .catch((loadError) => {
        if (cancelled) return
        setGoogleError(loadError.message || 'Google sign-in is unavailable right now.')
        setGoogleReady(false)
      })

    return () => {
      cancelled = true
    }
  }, [dispatch])

  const resetSharedErrors = () => {
    dispatch(clearError())
    setLocalError('')
    setGoogleError('')
  }

  const clearFieldError = (field) => {
    setErrors((current) => ({ ...current, [field]: '' }))
  }

  const switchAuthMethod = (method) => {
    setAuthMethod(method)
    setErrors({})
    setLocalError('')
    setGoogleError('')
    dispatch(clearError())

    if (method === 'password') {
      setOtp('')
      setOtpSent(false)
      setCountdown(0)
    }
  }

  const validatePasswordLogin = () => {
    const nextErrors = {}

    if (!form.email) nextErrors.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(form.email)) nextErrors.email = 'Enter a valid email'

    if (!form.password) nextErrors.password = 'Password is required'
    else if (form.password.length < 6) nextErrors.password = 'Password must be at least 6 characters'

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const validateMobile = () => {
    const nextErrors = {}
    const normalizedMobile = mobile.trim()

    if (!normalizedMobile) nextErrors.mobile = 'Mobile number is required'
    else if (!/^[6-9]\d{9}$/.test(normalizedMobile)) nextErrors.mobile = 'Enter a valid 10-digit mobile number'

    setErrors((current) => ({ ...current, mobile: nextErrors.mobile || '' }))
    return Object.keys(nextErrors).length === 0
  }

  const validateOtp = () => {
    const nextErrors = {}

    if (!otp.trim()) nextErrors.otp = 'OTP is required'
    else if (!/^\d{6}$/.test(otp.trim())) nextErrors.otp = 'Enter the 6-digit OTP'

    setErrors((current) => ({ ...current, otp: nextErrors.otp || '' }))
    return Object.keys(nextErrors).length === 0
  }

  const handlePasswordSubmit = (event) => {
    event.preventDefault()
    resetSharedErrors()

    if (!validatePasswordLogin()) return

    setActiveAction('password')
    dispatch(loginThunk({
      email: form.email.trim().toLowerCase(),
      password: form.password,
    }))
  }

  const handleSendOtp = async () => {
    resetSharedErrors()
    clearFieldError('otp')

    if (!validateMobile()) return

    setSendingOtp(true)

    try {
      await authApi.sendOtp({ mobile: mobile.trim() })
      setOtp('')
      setOtpSent(true)
      setCountdown(OTP_RESEND_SECONDS)
    } catch (sendError) {
      setLocalError(getErrorMessage(sendError, 'Could not send OTP. Please try again.'))
    } finally {
      setSendingOtp(false)
    }
  }

  const handleVerifyOtp = (event) => {
    event.preventDefault()
    resetSharedErrors()

    if (!validateMobile() || !validateOtp()) return

    setActiveAction('otp')
    dispatch(loginWithOtpThunk({
      mobile: mobile.trim(),
      otp: otp.trim(),
    }))
  }

  const activeError = error || localError || googleError

  return (
    <div className="min-h-screen flex bg-gray-50">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-950 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-cyan-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-48 h-48 bg-lime-500 rounded-full blur-3xl" />
        </div>

        <div className="relative">
          <div className="mb-16">
            <div className="inline-flex rounded-3xl border border-white/10 bg-white/5 p-3 shadow-2xl shadow-black/30 backdrop-blur-sm">
              <BrandLogo
                variant="fullDark"
                className="h-16 w-auto rounded-2xl object-contain"
              />
            </div>
          </div>

          <h2 className="text-4xl font-extrabold text-white mb-4 leading-tight">
            Manage your school
            <br />
            smarter, not harder.
          </h2>

          <p className="text-slate-400 text-lg leading-relaxed">
            The complete school management platform trusted by 500+ institutions across India.
          </p>
        </div>

        <div className="relative space-y-4">
          {[
            { icon: '✓', text: 'Real-time attendance tracking' },
            { icon: '✓', text: 'Automated fee collection & receipts' },
            { icon: '✓', text: 'Parent communication via mobile app' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {item.icon}
              </div>
              <span className="text-slate-300 text-sm">{item.text}</span>
            </div>
          ))}

          <div className="pt-6 border-t border-slate-700">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                {['A', 'R', 'S'].map((letter) => (
                  <div
                    key={letter}
                    className="w-8 h-8 rounded-full bg-primary-600 border-2 border-slate-900 flex items-center justify-center text-white text-xs font-bold"
                  >
                    {letter}
                  </div>
                ))}
              </div>
              <p className="text-slate-400 text-sm">
                Joined by <span className="text-white font-semibold">10,000+</span> educators
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <BrandLogo
              variant="fullLight"
              className="h-14 w-auto max-w-full object-contain"
            />
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-2">Welcome back</h1>
            <p className="text-gray-500">Sign in to your BrainCamp admin workspace</p>

            <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-700" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Admin portal only</p>
                  <p className="mt-1 text-sm text-blue-800">
                    Admins can sign in with password, mobile OTP, or Google. Teachers, students, and parents should continue using the BrainCamp mobile app.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {activeError && (
            <div className="mb-5 p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-600 text-xs font-bold">!</span>
              </div>
              <p className="text-sm text-red-700">{activeError}</p>
            </div>
          )}

          <div className="rounded-2xl border border-gray-200 bg-white p-1 mb-6 grid grid-cols-2 gap-1 shadow-sm">
            {[
              { key: 'password', label: 'Email & Password', icon: Mail },
              { key: 'otp', label: 'Mobile OTP', icon: Smartphone },
            ].map(({ key, label, icon: Icon }) => {
              const active = authMethod === key

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => switchAuthMethod(key)}
                  className={`flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-semibold transition ${
                    active
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{label}</span>
                </button>
              )
            })}
          </div>

          {authMethod === 'password' ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email address</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    autoComplete="email"
                    placeholder="admin@yourschool.com"
                    value={form.email}
                    onChange={(event) => {
                      resetSharedErrors()
                      setForm((current) => ({ ...current, email: event.target.value }))
                      clearFieldError('email')
                    }}
                    className={`input-field pl-10 ${errors.email ? 'border-red-400 focus:ring-red-500' : ''}`}
                  />
                </div>
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Password</label>
                <div className="relative">
                  <KeyRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={(event) => {
                      resetSharedErrors()
                      setForm((current) => ({ ...current, password: event.target.value }))
                      clearFieldError('password')
                    }}
                    className={`input-field pl-10 pr-10 ${errors.password ? 'border-red-400 focus:ring-red-500' : ''}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
              </div>

              <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-sm">
                <p className="text-amber-900">Forgot your password? Use mobile OTP to get back in.</p>
                <button
                  type="button"
                  onClick={() => switchAuthMethod('otp')}
                  className="shrink-0 font-semibold text-amber-700 hover:text-amber-800"
                >
                  Try OTP
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
              >
                {loading && activeAction === 'password'
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</>
                  : 'Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div className="rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3">
                <p className="text-sm font-medium text-blue-900">Login with Mobile OTP</p>
                <p className="mt-1 text-sm text-blue-800">
                  Enter your registered mobile number to receive a one-time password.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Mobile number</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 rounded-lg bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-500">
                    +91
                  </span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={10}
                    placeholder="9876543210"
                    value={mobile}
                    onChange={(event) => {
                      resetSharedErrors()
                      const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 10)
                      setMobile(digitsOnly)
                      clearFieldError('mobile')
                    }}
                    className={`input-field pl-16 ${errors.mobile ? 'border-red-400 focus:ring-red-500' : ''}`}
                  />
                </div>
                {errors.mobile && <p className="text-xs text-red-500 mt-1">{errors.mobile}</p>}
              </div>

              {!otpSent ? (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp}
                  className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
                >
                  {sendingOtp
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending OTP...</>
                    : 'Send OTP'}
                </button>
              ) : (
                <>
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-emerald-900">OTP sent to +91 {mobile}</p>
                        <p className="mt-1 text-sm text-emerald-800">
                          Enter the 6-digit code. You can request a fresh OTP after {countdown || OTP_RESEND_SECONDS}s.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">One-time password</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(event) => {
                        resetSharedErrors()
                        setOtp(event.target.value.replace(/\D/g, '').slice(0, 6))
                        clearFieldError('otp')
                      }}
                      className={`input-field text-center tracking-[0.4em] font-semibold ${errors.otp ? 'border-red-400 focus:ring-red-500' : ''}`}
                    />
                    {errors.otp && <p className="text-xs text-red-500 mt-1">{errors.otp}</p>}
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setOtp('')
                        setOtpSent(false)
                        setCountdown(0)
                        clearFieldError('otp')
                      }}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-800"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Change mobile
                    </button>

                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={sendingOtp || countdown > 0}
                      className="text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:text-gray-400"
                    >
                      {countdown > 0 ? `Resend in ${countdown}s` : 'Resend OTP'}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary py-3 text-base flex items-center justify-center gap-2"
                  >
                    {loading && activeAction === 'otp'
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying OTP...</>
                      : 'Verify & Sign In'}
                  </button>
                </>
              )}
            </form>
          )}

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-400">or continue with</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          {hasGoogleClientId ? (
            <div className={`rounded-2xl border border-gray-200 bg-white p-3 shadow-sm ${loading && activeAction === 'google' ? 'opacity-70' : ''}`}>
              <div ref={googleButtonRef} className="flex min-h-[44px] items-center justify-center" />
              {!googleReady && (
                <button
                  type="button"
                  disabled
                  className="w-full btn-secondary"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Preparing Google sign-in...
                </button>
              )}
              {loading && activeAction === 'google' && (
                <p className="mt-3 text-center text-sm text-gray-500">Completing Google sign-in...</p>
              )}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-4 text-center">
              <p className="text-sm font-medium text-gray-700">Google sign-in will appear once the Google client ID is configured for this environment.</p>
            </div>
          )}

          <p className="text-center text-sm text-gray-500 mt-6">
            Need help? <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary-600 font-medium hover:underline">Contact support</a>
          </p>
        </div>
      </div>
    </div>
  )
}
